import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import { parseIsoDate, toIsoDate } from './ecxDateUtils';

function EcxCalendarPopup({
    value,
    onSelect,
    onClose,
    min,
    max,
    anchorRef,
}) {
    const popupRef = useRef(null);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });

    const updatePosition = () => {
        const anchor = anchorRef?.current;
        const popup = popupRef.current;

        if (!anchor || !popup) {
            return;
        }

        const anchorRect = anchor.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();
        const viewportPadding = 12;

        let top = anchorRect.bottom + 6;
        let left = anchorRect.left;

        if (left + popupRect.width > window.innerWidth - viewportPadding) {
            left = window.innerWidth - popupRect.width - viewportPadding;
        }

        if (left < viewportPadding) {
            left = viewportPadding;
        }

        if (top + popupRect.height > window.innerHeight - viewportPadding) {
            top = anchorRect.top - popupRect.height - 6;
        }

        if (top < viewportPadding) {
            top = viewportPadding;
        }

        setPosition({ top, left });
    };

    useLayoutEffect(() => {
        updatePosition();
    }, [anchorRef]);

    useEffect(() => {
        if (!anchorRef?.current) {
            return undefined;
        }

        const handleReposition = () => updatePosition();
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [anchorRef]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const minDate = min ? parseIsoDate(min) : undefined;
    const maxDate = max ? parseIsoDate(max) : undefined;

    const handleChange = (nextValue) => {
        if (!(nextValue instanceof Date)) {
            return;
        }

        onSelect?.(toIsoDate(nextValue));
        onClose?.();
    };

    const handleToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (minDate && today < minDate) {
            return;
        }

        if (maxDate && today > maxDate) {
            return;
        }

        onSelect?.(toIsoDate(today));
        onClose?.();
    };

    const selectedDate = value ? parseIsoDate(value) : null;

    return (
        <div
            ref={popupRef}
            className="ecx-calendar-popup"
            style={{ top: position.top, left: position.left }}
            role="dialog"
            aria-modal="true"
            aria-label="Choose date"
        >
            <div className="ecx-calendar-popup__header">
                <div className="ecx-calendar-popup__title">
                    <i className="bi bi-calendar3"></i>
                    <span>Select date</span>
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                    onClick={onClose}
                    aria-label="Close calendar"
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            <div className="ecx-calendar-popup__body">
                <Calendar
                    className="ecx-calendar"
                    onChange={handleChange}
                    value={selectedDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    prev2Label={null}
                    next2Label={null}
                    prevLabel={<i className="bi bi-chevron-left"></i>}
                    nextLabel={<i className="bi bi-chevron-right"></i>}
                    showNeighboringMonth={false}
                />
            </div>

            <div className="ecx-calendar-popup__footer">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__footer-close-btn"
                    onClick={onClose}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel
                </button>
                <button type="button" className="btn btn-sm btn-accent rounded-pill" onClick={handleToday}>
                    <i className="bi bi-calendar-check me-1"></i>
                    Today
                </button>
            </div>
        </div>
    );
}

export default EcxCalendarPopup;
